import {
  forwardRef,
  useRef,
  useImperativeHandle,
  useEffect,
  useState,
} from "react";
import { useAtom } from "jotai";

import { MediaConnection } from "skyway-js";
import { Publication, RemoteDataStream } from "@skyway-sdk/core";
import type {
  LocalP2PRoomMember,
  P2PRoom,
  RemoteVideoStream,
  RoomPublication,
  RoomSubscription,
} from "@skyway-sdk/room";
import {
  nowInSec,
  LocalDataStream,
  SkyWayAuthToken,
  SkyWayContext,
  SkyWayRoom,
  SkyWayStreamFactory,
  uuidV4,
} from "@skyway-sdk/room";

import {
  body,
  bodyExpand,
  bodyExtendBack,
  text,
  flexWrap,
  // statusCheck,
  dots,
  dot,
  uiWrap,
  uiWrapExpand,
  audio,
  video,
  videoExpand,
  operatorName,
  cameraoff,
  cameraoffExpand,
  cameraoffExtendFront,
  avatorWrap,
  avatorWrapExpand,
  avatorWrapNoBorder,
  avator,
  avatorName,
} from "./style";
import { CallButton } from "components/CallButton";
import { ConfirmFinish } from "components/ConfirmFinish";
import { SettingModal } from "components/SettingModal";
import { Spacer } from "components/Spacer";
import {
  wsStatusAtom,
  myStreamAtom,
  siteCodeAtom,
  sessionIdAtom,
  myCameraAtom,
  requestUrlAtom,
  finishModalIsOpenAtom,
  anotherTabCallingAtom,
  timeoutModalIsOpenAtom,
  systemErrorModalIsOpenAtom,
  recallModalIsOpenAtom,
  recallCountAtom,
  useMyVideoAtom,
  meAtom,
  remoteAudioPublicationAtom,
  remoteVideoPublicationAtom,
  currentAudioDeviceIdAtom,
  currentVideoDeviceIdAtom,
} from "states";
import { OperatorVideoHandler, OperatorVideoProps } from "types";
import { Icon } from "icons/Icon";
import {
  setLocalStorage,
  getLocalStorage,
  clearLocalStorage,
} from "utils/localStorage";
import { Color } from "utils/variables";
import { usePost } from "hooks/usePost";

let localStream: MediaStream;
let audioLevelTimer: NodeJS.Timer;
let autoCancelTimer: NodeJS.Timer;
let wsMaintainTimer: NodeJS.Timer;
let destroyTimer: NodeJS.Timer;
let dataStream: LocalDataStream | undefined;
const minVolume: number = 0.01;
let socket: WebSocket;
let isReconnect: boolean = false;
let recallCountLocal = 0;
let myMemberIdentity: LocalP2PRoomMember;
let removeListener: (() => void) | undefined;

export const OperatorVideo = forwardRef<
  OperatorVideoHandler,
  OperatorVideoProps
>(({ isExpand, isFront, handleFront }: OperatorVideoProps, ref) => {
  const [room, setRoom] = useState<P2PRoom>();
  const [me, setMe] = useAtom(meAtom);
  const [, setRemoteAudioPublication] = useAtom(remoteAudioPublicationAtom);
  const [, setRemoteVideoPublication] = useAtom(remoteVideoPublicationAtom);
  const [currentAudioDeviceId] = useAtom(currentAudioDeviceIdAtom);
  const [currentVideoDeviceId] = useAtom(currentVideoDeviceIdAtom);
  const [myPeerId, setMyPeerId] = useState<string>("");
  const [mediaConnection] = useState<MediaConnection>();
  // const [videoPublication, setVideoPublication] = useState<RoomPublication>();
  const [operatorPeerId, setOperatorPeerId] = useState<string>("");
  const [confirmModalIsOpen, setConfirmModalIsOpen] = useState<boolean>(false);
  const [wsStatus, setWsStatus] = useAtom(wsStatusAtom);
  const [myStream, setMyStream] = useAtom(myStreamAtom);
  const [siteCode] = useAtom(siteCodeAtom);
  const [sessionId] = useAtom(sessionIdAtom);
  const [onMyCamera] = useAtom(myCameraAtom);
  const [, setRequestUrl] = useAtom(requestUrlAtom);
  const [, setFinishModalIsOpen] = useAtom(finishModalIsOpenAtom);
  const [, setAnotherTabCalling] = useAtom(anotherTabCallingAtom);
  const [, setIsOpenTimeoutModal] = useAtom(timeoutModalIsOpenAtom);
  const [, setIsOpenSystemErrorModal] = useAtom(systemErrorModalIsOpenAtom);
  const [, setIsOpenRecallModal] = useAtom(recallModalIsOpenAtom);
  const [recallCount, setRecallCount] = useAtom(recallCountAtom);
  const [, setUseMyVideo] = useAtom(useMyVideoAtom);
  const [onOperatorCamera, setOperatorCamera] = useState<boolean>(false);
  const wsRef = useRef<WebSocket>();
  const { axiosPost, responseStatus } = usePost();
  const [matchedCount, setMatchedCount] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<string>("");
  const [unload, setUnload] = useState<boolean>(false);

  // 相手のビデオを入れる要素
  const operatorVideo = useRef<HTMLVideoElement>(null);
  const operatorAudio = useRef<HTMLAudioElement>(null);

  const anchors = document.querySelectorAll('a:not([target="_blank"])');
  anchors.forEach((anchor) => {
    anchor.addEventListener("click", async function (e) {
      e.preventDefault(); // Stop the default action (navigation) from happening right away

      console.log("before navigation event");
      const dataConnectionSendData = {
        type: "unload",
      };

      const href = (e.currentTarget as HTMLAnchorElement).href;
      if (getLocalStorage("operatorPeerId"))
        dataStream?.write(dataConnectionSendData);
      console.log("room", room);
      await myMemberIdentity?.leave();

      // Perform any other necessary actions here

      // After everything is done, navigate to the new page:
      window.location.href = href;
    });
  });

  const handleUnload = () => {
    console.log("handleUnload");
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile) {
      setLocalStorage("myPeerId", "");
    }
    dataStream?.write({ type: "unload", message: "leave_room" });
    setUnload(true);
  };

  useEffect(() => {
    setCurrentPage(window.location.href);
    if (getLocalStorage("operatorPeerId")) {
      isReconnect = true;
      setOperatorPeerId(getLocalStorage("operatorPeerId") || "");

      const findRoom = async () => {
        try {
          const context = await SkyWayContext.Create(token);
          const room = await SkyWayRoom.Find(
            context,
            { id: getLocalStorage("operatorPeerId") || "" },
            "p2p"
          );
          setRoom(room);
          return room;
        } catch (error) {
          console.error(error);
        }
      };
      findRoom()
        .then((currentRoom) => {
          const myId = getLocalStorage("myPeerId") || "";
          const isExist = currentRoom?.members.some(
            (member) => member.id === myId
          );

          if (isExist) {
            setAnotherTabCalling(true);
            setUseMyVideo(false);
            setWsStatus("closed");
            isReconnect = false;
          } else {
            setAnotherTabCalling(false);
            setUseMyVideo(true);
            setWsStatus("matched");
          }
          me?.onFatalError.add(() => {
            cleanUpStorage();
            setWsStatus("closed");
          });
        })
        .catch((error) => {
          console.log(error);
        });
    } else {
      setUseMyVideo(true);
      socket = new WebSocket(process.env.REACT_APP_WS_API_HOST || "");
      wsRef.current = socket;

      // 接続時の処理
      socket.addEventListener("open", () => {
        setWsStatus("connected");
      });

      // エラー時の処理
      socket.addEventListener("error", () => {
        room?.dispose();
        setWsStatus("closed");
        setIsOpenSystemErrorModal(true);
        cleanUpStorage();
      });
      // 切断時の処理
      // socket.addEventListener("close", () => {
      // });
      // WebSocketからメッセージ受信時処理
      socket.addEventListener("message", (event) => {
        const res = JSON.parse(event.data);
        switch (res.cmd) {
          case "match":
            setWsStatus("matched");
            // 本物
            setOperatorPeerId(res.peer_id);
            setMatchedCount((count) => count + 1);
            // デバッグ用
            // setOperatorPeerId("CAYo4qmzmXUkVgoA");
            break;
          case "error":
            switch (res.error) {
              // 呼出要求でAPIエラー発生の場合
              case "call":
              case "chstate":
              case "cancel":
                recallCountLocal++;
                if (0 < recallCountLocal && recallCountLocal <= 5) {
                  setIsOpenRecallModal(true);
                } else if (recallCountLocal >= 6) {
                  recallCountLocal = 0;
                  setRecallCount(0);
                  setWsStatus("closed");
                  room?.onStreamPublished.removeAllListeners();
                  setIsOpenTimeoutModal(true);
                  cleanUpStorage();
                }
                break;
              // ステータスがmatch,connectだった場合に呼出要求した場合
              case "call_status":
                room?.dispose();
                setWsStatus("closed");
                setIsOpenSystemErrorModal(true);
                cleanUpStorage();
                break;
              default:
                break;
            }
            break;
          default:
            break;
        }
      });

      // WS維持のため8分おきに送信
      wsMaintainTimer = setInterval(() => {
        wsRef.current?.send(`{"cmd":"keepalive"}`);
      }, 480000);
    }

    // 異常終了
    window.addEventListener("beforeunload", (e) => {
      if (unload) return;
      console.log("before unload event");
      const dataConnectionSendData: object = {
        type: "unload",
      };
      if (getLocalStorage("operatorPeerId"))
        dataStream?.write(dataConnectionSendData);
      setUnload(true);
      e.preventDefault();
    });

    window.addEventListener("unload", () => {
      if (unload) return;
      console.log("unload event");
      handleUnload();
    });

    return () => {
      // アンマウント時にクローズする
      if (socket && socket.readyState === 1) {
        // コンソールエラー対策
        socket.close();
      }
      clearInterval(wsMaintainTimer);
      setWsStatus("closed");
      stopStreamedVideo();
      setRequestUrl("");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (unload) return;
    let url = window.location.href;
    document.body.addEventListener(
      "click",
      () => {
        requestAnimationFrame(() => {
          if (url === "" || currentPage === "") return;
          url !== currentPage && handleUnload();
          setCurrentPage(url);
        });
      },
      true
    );
  }, [currentPage, unload]);

  // メディアストリーム更新
  useEffect(() => {
    if (myStream) {
      localStream = myStream;
      mediaConnection?.replaceStream(myStream);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaConnection, myStream]);

  // マッチ判定が行われたらオペレータに通話
  useEffect(() => {
    if (wsStatus === "matched" && operatorPeerId) callOperator();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsStatus, operatorPeerId, matchedCount]);

  // カメラの状態が変わったら送信
  useEffect(() => {
    const dataConnectionSendData: object = {
      type: "camera",
      value: onMyCamera,
    };
    if (!dataStream) return;
    if (wsStatus === "calling") dataStream.write(dataConnectionSendData);
  }, [onMyCamera, wsStatus]);

  // 流入元POSTAPIのエラーハンドリング
  useEffect(() => {
    // 500の時のみ処理、200と401は何もしない
    if (responseStatus === 500) {
      const dataConnectionSendData: object = {
        type: "finish",
      };
      dataStream?.write(dataConnectionSendData);
      setTimeout(() => {
        room?.dispose();
        setWsStatus("closed");
      }, 1000);
      room?.onStreamPublished.removeAllListeners();
      setIsOpenTimeoutModal(true);
      cleanUpStorage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responseStatus]);

  // 繋ぎ直しのエラーハンドリング
  useEffect(() => {
    // 5回以内なら再試行
    if (0 < recallCount && recallCount <= 5) {
      // wsRef.current?.send(`{"cmd":"call","channel":"service_A","session_id":"${sessionId}","peer_id":"${myPeerId}"}`)
      // 最終的にはこれ↓
      wsRef.current?.send(
        `{"cmd":"call","channel":"${siteCode}","session_id":"${sessionId}","peer_id":"${myPeerId}"}`
      );
    } else if (recallCount === 0) {
      recallCountLocal = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recallCount]);

  // 親コンポーネントから呼ばれる関数
  useImperativeHandle(ref, () => ({
    callCancel() {
      callCancel();
    },
    callFinishModal() {
      setConfirmModalIsOpen(true);
    },
  }));

  // マッチした時の処理
  const matching = async (e?: MouseEvent) => {
    e?.stopPropagation();
    setWsStatus("matching");
    // デバッグ中のeslint対策
    // console.log(siteCode)
    // wsRef.current?.send(
    //   `{"cmd":"call","channel":"service_A","session_id":"${sessionId}","peer_id":"${myPeerId}"}`
    // );
    // 最終的にはこれ↓
    wsRef.current?.send(
      `{"cmd":"call","channel":"${siteCode}","session_id":"${sessionId}","peer_id":"${myPeerId}"}`
    );

    autoCancelTimer = setTimeout(() => {
      callCancel();
      setIsOpenTimeoutModal(true);
    }, 60000);
  };

  // メディアストリームを止める
  const stopStreamedVideo = () => {
    localStream?.getTracks().forEach(function (track: { stop: () => void }) {
      track.stop();
    });
    setMyStream(localStream);
  };

  // peer作成
  // => room作成
  const token = new SkyWayAuthToken({
    jti: uuidV4(),
    iat: nowInSec(),
    exp: nowInSec() + 60 * 60 * 24,
    scope: {
      app: {
        id: process.env.REACT_APP_SKYWAY_APP_KEY || "",
        turn: true,
        actions: ["read"],
        channels: [
          {
            id: "*",
            name: "*",
            actions: ["write"],
            members: [
              {
                id: "*",
                name: "*",
                actions: ["write", "create", "signal", "updateMetadata"],
                publication: {
                  actions: ["write", "create", "updateMetadata"],
                },
                subscription: {
                  actions: ["write", "create"],
                },
              },
            ],
            sfuBots: [
              {
                actions: ["write"],
                forwardings: [
                  {
                    actions: ["write"],
                  },
                ],
              },
            ],
          },
        ],
      },
    },
  }).encode(process.env.REACT_APP_SKYWAY_SECRET_KEY || "");

  // 通話キャンセル
  const callCancel = () => {
    console.log("call cancel");
    wsRef.current?.send('{"cmd":"cancel"}');
    removeListener?.();
    setMe(undefined);
    setRemoteAudioPublication(undefined);
    setRemoteVideoPublication(undefined);
    room?.onStreamPublished.removeAllListeners();
    setWsStatus("closed");
    clearTimeout(autoCancelTimer);
  };

  const connectToRoom = async () => {
    try {
      const context = await SkyWayContext.Create(token);
      const room = await SkyWayRoom.Find(
        context,
        { id: operatorPeerId },
        "p2p"
      );
      setRoom(room);

      const audio = await SkyWayStreamFactory.createMicrophoneAudioStream({
        deviceId: currentAudioDeviceId || "",
      });
      const video = await SkyWayStreamFactory.createCameraVideoStream({
        deviceId: currentVideoDeviceId || "",
      });
      dataStream = await SkyWayStreamFactory.createDataStream();
      myMemberIdentity = await room.join();
      setMe(myMemberIdentity);

      const remoteAudioPublication = await myMemberIdentity.publish(audio);
      const remoteVideoPublication = await myMemberIdentity.publish(video);
      setRemoteAudioPublication(remoteAudioPublication);
      setRemoteVideoPublication(remoteVideoPublication);
      await myMemberIdentity.publish(dataStream);

      // setVideoPublication(remoteVideoPublication);
      setMyPeerId(myMemberIdentity.id);

      console.log("me, before on stream published", myMemberIdentity);
      const trackStreamPublished = room?.onStreamPublished.add(async (e) => {
        if (e.publication.publisher.id === myMemberIdentity?.id) return;
        const isAlreadySubscribed = e.publication.subscriptions.some(
          (subscription) => subscription.subscriber.id === myMemberIdentity.id
        );
        console.log("is alreadySubscribed", isAlreadySubscribed);
        console.log("publication publisher id", e.publication.publisher.id);
        if (e.publication.contentType === "data") {
          const { stream } =
            (await myMemberIdentity?.subscribe<RemoteDataStream>(
              e.publication.id
            )) || {};
          stream?.onData.add((args) => {
            const data = args as { type: string; value: any };
            switch (data.type) {
              case "camera":
                console.log("camera", data.value);
                setOperatorCamera(data.value);
                break;
              case "request":
                setRequestUrl(data.value);
                setTimeout(() => {
                  setRequestUrl("");
                }, 7000);
                break;
              case "unload":
                room?.dispose();
                cleanUpStorage();
                break;
              case "finish":
                finishProcess();
                break;
              default:
                break;
            }
          });
        } else {
          setOperatorVideo(e.publication, myMemberIdentity);
        }
      });
      if (trackStreamPublished) {
        removeListener = trackStreamPublished.removeListener;
      }
    } catch (error) {
      console.error(error);
    }
  };
  // 発話処理
  const callOperator = async () => {
    if (isReconnect) {
      await generateDataConnection();
    } else {
      await connectToRoom();
    }
  };

  // DataConnection受信処理
  const generateDataConnection = async () => {
    try {
      const context = await SkyWayContext.Create(token);
      const room = await SkyWayRoom.Find(
        context,
        { id: operatorPeerId },
        "p2p"
      );
      const me = await room.join();
      setMe(me);
      const myId = me?.id;
      const operator = room?.members.find((member) => member.id !== myId);

      dataStream = await SkyWayStreamFactory.createDataStream();
      const publication = await me?.publish(dataStream);

      operator?.publications.forEach(async (publication) => {
        if (publication.contentType === "data") {
          const { stream } = (await me?.subscribe(publication.id)) || {};
          (stream as RemoteDataStream).onData.add((args) => {
            const data = args as { type: string; value: any };
            switch (data.type) {
              case "camera":
                setOperatorCamera(data.value);
                break;
              case "request":
                setRequestUrl(data.value);
                setTimeout(() => {
                  setRequestUrl("");
                }, 7000);
                break;
              case "unload":
                room?.dispose();
                cleanUpStorage();
                break;
              case "finish":
                finishProcess();
                break;
              default:
                break;
            }
          });
        } else {
          setOperatorVideo(publication, me);
        }
      });

      publication.onSubscribed.add(() => {
        if (isReconnect) {
          setTimeout(async () => {
            const dataConnectionSendData: object = {
              type: "unload",
            };
            dataStream?.write(dataConnectionSendData);
            const audio = await SkyWayStreamFactory.createMicrophoneAudioStream(
              {
                deviceId: currentAudioDeviceId || "",
              }
            );
            const video = await SkyWayStreamFactory.createCameraVideoStream({
              deviceId: currentVideoDeviceId || "",
            });
            const newRemoteAudioPublication = await me?.publish(audio);
            const newRemoteVideoPublication = await me?.publish(video);
            setRemoteAudioPublication(newRemoteAudioPublication);
            setRemoteVideoPublication(newRemoteVideoPublication);
            // setVideoPublication(newRemoteVideoPublication);

            isReconnect = false;
          }, 2000);
        }
      });
    } catch (error) {
      console.error("error", error);
    }
  };

  // 相手の音量を拾う
  const setRtpTimer = (
    customerAudioSubscription: RoomSubscription | undefined
  ) => {
    audioLevelTimer = setInterval(() => {
      customerAudioSubscription
        ?.getRTCPeerConnection()
        ?.getStats()
        .then((stats) => {
          stats.forEach((stat) => {
            if (stat.type === "inbound-rtp" && stat.kind === "audio") {
              levelStyle(stat.audioLevel);
            }
          });
        });
    }, 500);
  };

  // 音量でスタイルを当てる
  const levelStyle = (vol: number) => {
    const level = [
      ...(document.getElementsByClassName(
        "operator-sbeolradeegrde"
      ) as HTMLCollectionOf<HTMLElement>),
    ];
    if (level.length) {
      if (vol < minVolume) vol = 0;
      if (vol > 0) {
        for (let i = 0; i < level.length; i++) {
          level[i].style.borderColor = `${Color.PaleGreen}`;
        }
      } else {
        for (let i = 0; i < level.length; i++) {
          level[i].style.borderColor = `${Color.White}`;
        }
      }
    }
  };

  // 通話が繋がった時の処理
  const setOperatorVideo = async (
    publication: Publication | RoomPublication,
    currentMe?: LocalP2PRoomMember
  ) => {
    console.log("set operator video");
    const localPerson = currentMe ?? me;

    if (publication.publisher.id === localPerson?.id) return;
    const currentSubscriptionList = localPerson?.subscriptions;
    const alreadySubscribed = currentSubscriptionList?.some(
      (subscription) => subscription.publication.id === publication.id
    );
    if (alreadySubscribed) return;
    const postData: object = {
      inflow_url: window.location.href,
      session_id: sessionId,
    };
    const dataConnectionSendData: object = {
      type: "camera",
      value: onMyCamera,
    };
    try {
      const { subscription, stream } =
        (await localPerson?.subscribe<RemoteVideoStream>(publication.id)) || {};

      if (operatorVideo.current !== null && operatorAudio.current !== null) {
        switch (stream?.track.kind) {
          case "video":
            stream?.attach(operatorVideo.current);
            dataStream?.write(dataConnectionSendData);
            break;
          case "audio":
            stream?.attach(operatorAudio.current);
            setRtpTimer(subscription);
            break;
          default:
            return;
        }
      }

      // 8分おきの送信を解除
      // 10分待った時の自動キャンセル解除
      clearInterval(wsMaintainTimer);
      clearTimeout(autoCancelTimer);
      // WS切断
      wsRef.current?.close();
      setWsStatus("calling");
      // 最初の1回だけ流入元保存APIを投げる
      if (!getLocalStorage("operatorPeerId")) {
        axiosPost("/inflow", postData);
      }
      // ローカルストレージ
      setLocalStorage("operatorPeerId", operatorPeerId);
      setLocalStorage("myPeerId", currentMe?.id);

      // setMediaConnection(mc)
      publication?.onCanceled.add(() => {
        setWsStatus("closed");
        clearInterval(audioLevelTimer);
        clearTimeout(destroyTimer);
      });
    } catch (error) {
      console.error(error);
      // setWsStatus("closed");
      clearInterval(audioLevelTimer);
      clearTimeout(destroyTimer);
    }
  };

  // 小画面をクリックした時
  const clickBody = () => {
    if (isExpand && isFront) handleFront();
  };

  // 通話の正規終了
  const callFinish = () => {
    const dataConnectionSendData: object = {
      type: "finish",
    };
    dataStream?.write(dataConnectionSendData);

    finishProcess();
  };

  // 通話終了処理
  const finishProcess = () => {
    // peer接続を切断
    destroyTimer = setTimeout(() => {
      room?.dispose();
      setWsStatus("closed");
    }, 2000);
    // モーダル開閉
    setConfirmModalIsOpen(false);
    setFinishModalIsOpen(true);
    cleanUpStorage();
    setRequestUrl("");
  };

  // 初期化
  const cleanUpStorage = () => {
    // ローカルストレージクリア
    clearLocalStorage();
    // 通話状態の変更
    setAnotherTabCalling(false);
  };

  return (
    // sbeolradeegrde→sdkでサイトとクラス名が被らないように調整
    // stage led border のアナグラムで生成
    <>
      <div
        className={`
          ${body}
          ${isExpand ? ` ${bodyExpand}` : ""}
          ${isExpand && !isFront ? ` ${bodyExtendBack}` : ""}
          ${isExpand && isFront ? " operator-sbeolradeegrde" : ""}
        `}
        id="operator-video"
        onClick={() => clickBody()}
      >
        <video
          ref={operatorVideo}
          autoPlay
          playsInline
          className={`${video} ${isExpand ? videoExpand : ""}`}
        />
        <audio className={`${audio}`} ref={operatorAudio} autoPlay controls />
        {wsStatus === "calling" && !onOperatorCamera && (
          <div
            className={`
              ${cameraoff}
              ${isExpand ? ` ${cameraoffExpand}` : ""}
              ${isExpand && isFront ? ` ${cameraoffExtendFront}` : ""}
            `}
          >
            <div
              className={`
                ${avatorWrap}
                ${isExpand ? ` ${avatorWrapExpand}` : ""}
                ${
                  isExpand && isFront
                    ? ` ${avatorWrapNoBorder}`
                    : " operator-sbeolradeegrde"
                }
              `}
            >
              {isExpand ? (
                <>
                  {isFront ? (
                    <Icon name="Operator" size={39} className={avator} />
                  ) : (
                    <Icon name="Operator" size={57} className={avator} />
                  )}
                </>
              ) : (
                <Icon name="Operator" size={39} className={avator} />
              )}
            </div>
            {isExpand && !isFront && (
              <>
                <Spacer size={15} />
                <p className={avatorName}>オペレーター</p>
              </>
            )}
          </div>
        )}
        <div className={isExpand ? uiWrapExpand : uiWrap}>
          {/* デバッグ用にステータスを表示 */}
          {/* <p className={statusCheck}>{wsStatus}</p> */}
          {wsStatus === "calling" && !(isExpand && !isFront) && (
            <p className={operatorName}>オペレーター</p>
          )}
          {(wsStatus === "closed" || wsStatus === "connected") && (
            <p className={text}>
              タップして
              <br />
              オペレーターに繋ぐ
            </p>
          )}
          {(wsStatus === "matching" || wsStatus === "matched") && (
            <p className={text}>
              オペレーターを
              <br />
              呼び出し中
            </p>
          )}
          <Spacer size={7} vertrical />
          {wsStatus === "connected" && (
            <div className={flexWrap}>
              <CallButton onClick={(e) => matching(e)} />
            </div>
          )}
          {(wsStatus === "matching" || wsStatus === "matched") && (
            <div className={`${flexWrap} ${dots}`}>
              <div className={dot}></div>
              <div className={dot}></div>
              <div className={dot}></div>
              <div className={dot}></div>
              <div className={dot}></div>
              <div className={dot}></div>
            </div>
          )}
        </div>
      </div>
      <SettingModal
        isOpen={confirmModalIsOpen}
        onRequestClose={() => setConfirmModalIsOpen(false)}
        label="confirmFininsh"
      >
        <ConfirmFinish onFinish={() => callFinish()} />
      </SettingModal>
    </>
  );
});
